import { createGlobalStyle } from "styled-components";

import { colorIsLight, lightenDarkenColor } from "Utilities/Colors/colorUtils";

export const colors = {
  // ################
  // Legacy & Misc
  // ################

  fnGreen: "#00B44E",
  fnDkBlueGray: "#2f3f4f",
  fnRed: "#f0243a",
  fnRedNotice: "#d5a223",
  fnDkRed: "#802428",
  fnPurple: "#8B169C",
  fnPurpleLt: "rgba(90, 22, 156, .13)",
  fnYellow: "#fccf6b",
  fnYellowNotice: "#f7f0b6",
  wofOrange: "#ed7722",
  transparent: "#ffffff00",
  white: "#ffffff",

  // ################
  // Blues
  // ################

  fnDarkBlue: "#016099",
  fnBlue: "#278DC1", // equivalent to fnBlue600
  fnBlueHover: "#1c6e97", // equivalent to fnBlue700
  fnDkBlue: "#073a5f", // equivalent to fnBlue800

  fnBlue100: "#ecf4f9", // hsl(200, 51%, 95%)
  fnBlue200: "#c4e0ed", // hsl(200, 54%, 85%)
  fnBlue300: "#9bcbe4", // hsl(200, 57%, 75%)
  fnBlue400: "#70b8db", // hsl(200, 60%, 65%)
  fnBlue500: "#44a4d5", // hsl(200, 63%, 55%)
  fnBlue600: "#278DC1", // hsl(200, 66%, 45%)
  fnBlue700: "#1c6e97", // hsl(200, 69%, 35%)
  fnBlue800: "#073a5f", // hsl(205, 86%, 20%)
  fnBlue900: "#0a3043", // hsl(200, 75%, 15%)

  // ################
  // Grays
  // ################

  fnLtGray: "#eceff0", // equivalent to fnGray100
  fnBorderGray: "#e5e7eb",
  fnHeaderGray: "#F9FBFC",
  fnGray: "#98a4a5", // equivalent to fnGray400
  fnDkGray: "#828c8d", // equivalent to fnGray500
  fnTabSelectedGray: "#4d5f68",
  fnScrollbarDarkGray: "#4a5e72",
  fnBorderGray2: "#e0e0e0",

  fnGray50: "hsl(195, 12%, 97%)", // hsl(195, 12%, 98%)
  fnGray100: "#eceff0", // hsl(195, 12%, 93%)
  fnGray200: "#cfd3d3", // hsl(180, 4%, 82%)
  fnGray300: "#b3babc", // hsl(193, 6%, 72%)
  fnGray400: "#98a4a5", // hsl(185, 7%, 62%)
  fnGray500: "#828c8d", // hsl(185, 5%, 53%)
  fnGray600: "#5d7479", // hsl(191, 13%, 42%)
  fnGray700: "#455a5f", // hsl(192, 16%, 32%)
  fnGray800: "#2d3f43", // hsl(191, 20%, 22%)
  fnGray900: "#182325", // hsl(189, 21%, 12%)

  // ################
  // Blacks
  // ################

  fnInputBlack: "#333",

  // @todo where to put these? a `features` object?
  // ################
  // DioSync Purple
  // ################
  fnDiosyncPurple50: "#f6f2fc",
  fnDiosyncPurple100: "#eee4f9",
  fnDiosyncPurple200: "#dbc6f3",
  fnDiosyncPurple300: "#ccaced",
  fnDiosyncPurple400: "#b886e5",
  fnDiosyncPurple500: "#a661dc",
  fnDiosyncPurple600: "#8c40c1",
  fnDiosyncPurple700: "#692f92",
  fnDiosyncPurple800: "#481e65",
  fnDiosyncPurple900: "#2c0f40",
  fnDiosyncPurple950: "#1b0729",
};

export const marketingColors = {
  // primary colors

  // blues
  fnBlue: "#2867ac",
  blue: "#4e99d1",
  darkBlue: "#182941",
  lightBlue: "#c7e2f2",

  //misc
  red: "#d9705e",
  green: "#c8d3ac",
  yellow: "#ecc27b",
  grey: "#f2f2f1",
  gray: "#f2f2f1",
  completed: "#84c760",

  // secondary colors
  sky: "#90c4e8",
  maize: "#fbdc91",
  celadon: "#adb796",
  apricot: "#e09f8a",
  taupe: "#6f6c6a",

  // derivatives
  faintTaupe: "#6f6c6a33",
  opaqueFaintTaupe: "#e2e2e1",
};

export const colorsByFunction = {
  textLight: colors.fnGray100,
  textPrimary: "#15436a",
  textSecondaryLight: colors.fnGray500,
  textSecondary: "#586b74",
  textSecondaryDark: colors.fnTabSelectedGray,
  textAlmostBlack: "#384a5d",
  textBlack: "#383838",
  textMuted: colors.fnGray400,
  textWarning: "#b45309",
  warningBorder: "#f59e0b",
  warningBg: "#fef9e7",
  divider: "#98A4A5",
  defaultPhoto: "#03609a",

  // The chrome a page is built out of. Named by the job so two of them can
  // share a value without fusing — borderLight and surfaceHover are both
  // fnGray100 today, and only these names keep them separately changeable.
  surface: colors.white,
  surfaceSunken: colors.fnGray50,
  surfaceHover: colors.fnGray100,
  border: colors.fnBorderGray,
  borderLight: colors.fnGray100,

  // Badge pairs — a tint and a text colour that reads on it. Use them as
  // pairs; a bg from one with a colour from another lands somewhere neither
  // was checked for contrast. "updated" is deliberately absent: warningBg and
  // textWarning above already are that pair, and a second name for them is how
  // the two amber palettes got started.
  //
  // Green is the only pair spelled out here. Everything else points at
  // something already in `colors` — there just isn't a light or a dark green
  // in the palette to point at.
  addedBg: "#e7f8ef",
  addedText: "#116937",
  removedBg: "#fbe9ea",
  removedText: colors.fnDkRed,
  mergedBg: colors.fnPurpleLt,
  mergedText: colors.fnPurple,
  importBg: colors.fnBlue100,
  importText: colors.fnBlue800,
  neutralBg: marketingColors.grey,
  neutralText: colors.fnDkBlueGray,
};

/**
 * Resolves a color reference (theme key from `colors` or `colorsByFunction`,
 * or a raw CSS color value) to a string usable in CSS.
 */
export const resolveColor = (value) =>
  colors[value] || colorsByFunction[value] || value;

export const navColors = {
  groups: "#0CA4EC",
  database: "#EE6565",
  giving: "#1EC376",
  signups: "#8F6BE7",
  tools: "#59BBBF",
};

const accentDiff = 60;
const lightDarkThresholdOffset = 27;
export const navAccentColors = {
  database: [
    lightenDarkenColor(
      navColors.database,
      colorIsLight(navColors.database, lightDarkThresholdOffset)
        ? -accentDiff
        : accentDiff
    ),
    lightenDarkenColor(
      navColors.database,
      colorIsLight(navColors.database, lightDarkThresholdOffset)
        ? accentDiff
        : -accentDiff
    ),
  ],
  groups: [
    lightenDarkenColor(
      navColors.groups,
      colorIsLight(navColors.groups, lightDarkThresholdOffset)
        ? -accentDiff
        : accentDiff
    ),
    lightenDarkenColor(
      navColors.groups,
      colorIsLight(navColors.groups, lightDarkThresholdOffset)
        ? accentDiff
        : -accentDiff
    ),
  ],
  giving: [
    lightenDarkenColor(
      navColors.giving,
      colorIsLight(navColors.giving, lightDarkThresholdOffset)
        ? -accentDiff
        : accentDiff
    ),
    lightenDarkenColor(
      navColors.giving,
      colorIsLight(navColors.giving, lightDarkThresholdOffset)
        ? accentDiff
        : -accentDiff
    ),
  ],
  signups: [
    lightenDarkenColor(
      navColors.signups,
      colorIsLight(navColors.signups, lightDarkThresholdOffset)
        ? -accentDiff
        : accentDiff
    ),
    lightenDarkenColor(
      navColors.signups,
      colorIsLight(navColors.signups, lightDarkThresholdOffset)
        ? accentDiff
        : -accentDiff
    ),
  ],
  tools: [
    lightenDarkenColor(
      navColors.tools,
      colorIsLight(navColors.tools, lightDarkThresholdOffset)
        ? -accentDiff
        : accentDiff
    ),
    lightenDarkenColor(
      navColors.tools,
      colorIsLight(navColors.tools, lightDarkThresholdOffset)
        ? accentDiff
        : -accentDiff
    ),
  ],
};

export const gradients = {
  legacyDarkBlue: `linear-gradient(to right, #13426b, #124976, #0f5182, #0a588d, ${colors.fnDarkBlue});`,
};

function createCSSColorVars() {
  return Object.keys(colors).map((color) => {
    return `--${color}: ${colors[color]};`;
  });
}

function createMarketingCSSColorVars() {
  return Object.keys(marketingColors).map((color) => {
    const varColor = `--marketing${capitalize(color)}`;
    return `${varColor}: ${marketingColors[color]};`;
  });
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const CSSColorVariables = createGlobalStyle`
  :root {
    ${createCSSColorVars()}
    ${createMarketingCSSColorVars()}
  }
`;
